import {
	addHours,
	endOfDay,
	format,
	formatISO,
	parseISO,
	setHours,
	setMinutes,
	startOfDay,
} from 'date-fns';

/**
 * date-fns-backed date/time helpers.
 *
 * The rest of the app still uses moment (see `utils/datetime.js`);
 * this file is the entry point for the ongoing migration. New code that
 * needs raw JS Date manipulation should import from here rather than
 * calling `new Date()` / `setHours()` directly.
 *
 * All helpers return ISO strings so they slot straight into GraphQL
 * DateTime scalars.
 */

/**
 * ISO string for the start of today in the runtime timezone.
 * Example: `"2026-07-16T00:00:00.000Z"` when today is 2026-07-16 local.
 */
export const todayStartIso = () => formatISO(startOfDay(new Date()));

/**
 * ISO string for the end of today in the runtime timezone.
 * Example: `"2026-07-16T23:59:59.999Z"`.
 */
export const todayEndIso = () => formatISO(endOfDay(new Date()));

/**
 * `{ from, to }` ISO pair spanning today (start-of-day → end-of-day).
 * Consumed by `deadheadSearch` / `rideSearch` filters that expect
 * `departureFromDateTime` + `departureToDateTime`.
 */
export const todayIsoWindow = () => ({
	from: todayStartIso(),
	to: todayEndIso(),
});

/**
 * `{ from, to }` ISO pair spanning the next 48 hours (now → now + 48h).
 * Fallback for the deadhead picker when there's no anchor timestamp yet.
 */
export const next48hIsoWindow = () => ({
	from: formatISO(new Date()),
	to: formatISO(addHours(new Date(), 48)),
});

/**
 * `{ from, to }` ISO pair spanning `hours` hours before and after the
 * given anchor ISO. Used by the deadhead picker to look for deadheads
 * departing near the incident time.
 *
 * Returns `null` if the anchor can't be parsed — callers should fall
 * back to `next48hIsoWindow()`.
 *
 * @param {string} anchorIso ISO timestamp
 * @param {number} hours symmetric window half-width in hours
 */
export const isoWindowAround = (anchorIso, hours) => {
	if (!anchorIso) return null;
	const d = parseISO(anchorIso);
	if (Number.isNaN(d.getTime())) return null;
	return { from: formatISO(addHours(d, -hours)), to: formatISO(addHours(d, hours)) };
};

/**
 * ISO string for `today at HH:MM` in the runtime timezone. Used by stub
 * fixtures so their timestamps stay anchored to "today" without needing
 * a fixed reference date.
 *
 * @param {number} hour   0-23
 * @param {number} minute 0-59 (default 0)
 */
export const todayAtIso = (hour, minute = 0) =>
	formatISO(setMinutes(setHours(startOfDay(new Date()), hour), minute));

/**
 * ISO string for `now + hours` in the runtime timezone.
 */
export const inHoursIso = (hours) => formatISO(addHours(new Date(), hours));

/**
 * `true` when the runtime locale prefers month-day-year (US-style dates).
 * Falls back to `false` (European day/month/year) for unknown locales.
 */
const isUsLocale = () => {
	const locale = typeof navigator !== 'undefined' && (navigator.language || navigator.userLanguage);
	return typeof locale === 'string' && locale.toLowerCase().startsWith('en-us');
};

/**
 * Human-readable departure formatter for pickers.
 *
 * Detects the runtime locale via `navigator.language`:
 * - **en-US**: `MM/dd/yyyy 'at' hh:mma`  → `07/16/2026 at 08:07AM`
 * - **any other**: `dd/MM/yyyy 'at' HH:mm` → `16/07/2026 at 08:07`
 *
 * Returns `''` for null / invalid ISO input.
 *
 * @param {string | null | undefined} iso UTC-ish ISO timestamp
 */
export const formatDepartureIso = (iso) => {
	if (!iso) return '';
	const d = parseISO(iso);
	if (Number.isNaN(d.getTime())) return '';
	const pattern = isUsLocale() ? "MM/dd/yyyy 'at' hh:mma" : "dd/MM/yyyy 'at' HH:mm";
	return format(d, pattern);
};

/**
 * Human-readable formatter used across the breakdown Summary step.
 *
 * Shape (matches the design across every reason and every timestamp row —
 * Incident time, Original driver absence start, Post-incident driving start,
 * Cushioning start/end, Parallel ride Departure, Replacement ETD/ETA):
 *
 *   `Jul 20, 2026 - 10:31 AM`
 *
 * The value is rendered in the runtime local timezone.
 *
 * Accepts:
 * - a UTC ISO string (`2026-07-20T10:31:00Z`)
 * - the `YYYY-MM-DDTHH:mm` shape emitted by `combineDateTime`
 *
 * Returns `''` for null / undefined / empty / invalid input.
 *
 * @param {string | null | undefined} value
 * @returns {string}
 */
export const formatSummaryDateTime = (value) => {
	if (!value) return '';
	const d = parseISO(value);
	if (Number.isNaN(d.getTime())) return '';
	return format(d, 'MMM d, yyyy - h:mm a');
};

/**
 * Reset a local wall-clock timestamp into a UTC ISO string suitable for
 * `DateTime` scalars.
 *
 * Mirrors `utils/datetime.resetTimestampToUtc` (moment-based) — this
 * variant is date-fns-only so it can be imported without pulling moment
 * into feature bundles that have finished the migration.
 *
 * Accepts anything `parseISO` understands, including the naked
 * `YYYY-MM-DDTHH:mm` shape emitted by `combineDateTime` (parsed as local
 * time when there's no timezone suffix). Returns `''` for null / empty /
 * invalid input.
 *
 * @param {string | null | undefined} value
 * @returns {string}
 */
export const resetTimestampToUtc = (value) => {
	if (!value) return '';
	const d = parseISO(value);
	if (Number.isNaN(d.getTime())) return '';
	return d.toISOString();
};
